import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject, Optional,
    SkipSelf,
    ViewEncapsulation,
    computed,
    effect,
    forwardRef,
    output,
    signal,
    viewChild
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { AppService } from "src/services/app.service";
import { getFractions } from "src/util/formatting/currency";
import { VIconComponent } from "src/app/components/v-icon/v-icon.component";
import { InputFormWrapper, VContainerComponent } from "../wrapper.interface";
import { V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { InputCurrencyConfig } from "./input-currency.config";

@Component({
    selector:      'v-input-currency',
    templateUrl:   './input-currency.component.haml',
    styleUrls:     ['./input-currency.component.sass', '../input.style.sass'],
    encapsulation: ViewEncapsulation.None,
    providers:     [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VInputCurrencyComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VInputCurrencyComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VInputCurrencyComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [CommonModule, ReactiveFormsModule, TranslateModule, VIconComponent]
})
export class VInputCurrencyComponent extends InputFormWrapper<InputCurrencyConfig, number> {
    protected _baseConfig: InputCurrencyConfig = InputCurrencyConfig.baseTheme.Default;
    private readonly _focus = signal<boolean>(false);

    public readonly inputId = signal<string>(undefined);
    public readonly name = signal<string>(undefined);
    public readonly onFocus = output({alias: 'focus'});
    public readonly onBlur = output({alias: 'blur'});
    public readonly onInput = output<Event>({alias: 'input'});
    private readonly el = viewChild<ElementRef<HTMLInputElement>>('inputfield');

    private readonly locale = toSignal(this.appService.$Locale);
    private readonly decimalSeparator = computed(() => {
        return (1.1).toLocaleString(this.locale()).substring(1, 2);
    });

    public readonly currencySymbol = computed(() => {
        const locale = this.locale();
        return (0).toLocaleString(locale, {
            style:                 'currency',
            currency:              this.appService.defaultCurrency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).replace(/\d/g, '').trim();
    });

    public readonly suffix = computed(() => this.currencySymbol());

    constructor(
        @Optional() protected controlContainer: ControlContainer,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent: VContainerComponent,
        protected appService: AppService
    ) {
        super(controlContainer, parent);
        effect(() => {
            // read locale to ensure this effect re-runs when the locale changes
            this.locale();
            this.updateInputfield(this.value);
        });

        effect(() => {
            const focused = this._focus();
            if (!focused)this.onBlur.emit();
            else this.onFocus.emit();
        });
    }
    public inputElement = computed(() => this.el()?.nativeElement);

    public focus(options?: FocusOptions) {
        this._focus.set(true);
        if (!this.value && this.inputElement())this.inputElement().value = this.formatValue(0);
        this.inputElement()?.focus(options);
    }

    public blur() {
        this._focus.set(false);
        this.inputElement()?.blur();
    }

    public onInputFocus(_event: Event) {
        this.focus();
    }

    public onInputBlur(_event: Event) {
        this.blur();
        this.onModelTouched();
    }

    public onInputKeydown(event: KeyboardEvent) {
        switch(event.key) {
            case 'c': if (event.ctrlKey || event.metaKey) break;
            case 'v': if (event.ctrlKey || event.metaKey) break;

            case 'ArrowUp': {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;
                this.selectChunk();
                const {start, end} = this.caret();
                const inputValue = input.value;
                const decimalStart = inputValue.length - this.fractions - 1;
                const numberValue = Number.parseInt((inputValue.substring(start, end).replace(/[^0-9\-]/g, ''))) + 1;
                const adjustedValue = start > decimalStart ? numberValue.toString().at(-1) : numberValue.toString();
                const newValue = inputValue.substring(0, start) + adjustedValue + inputValue.substring(end);
                this.writeValue(this.parseStringToNumber(newValue));
                input.setSelectionRange(start, end);
                break;
            }

            case 'ArrowDown': {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;
                this.selectChunk();
                const {start, end} = this.caret();
                const inputValue = input.value;
                const numberValue = Number.parseInt((inputValue.substring(start, end).replace(/[^0-9\-]/g, ''))) - 1;
                const decimalStart = inputValue.length - this.fractions - 1;
                const adjustedValue = (start > decimalStart && numberValue < 0 ? 9 : numberValue).toString();
                const newValue = inputValue.substring(0, start) + adjustedValue + inputValue.substring(end);
                this.writeValue(this.parseStringToNumber(newValue));
                input.setSelectionRange(start, end);
                break;
            }

            case 'ArrowLeft': {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;
                const {start} = this.caret();
                input.setSelectionRange(start - 1, start - 1);
                this.selectChunk();
                break;
            }

            case 'ArrowRight': {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;
                const {end} = this.caret();
                input.setSelectionRange(end + 1, end + 1);

                // allow cursor to go to the end to add digit shifting otherwise select chunk
                if (end + 1 > input.value.length) return;
                this.selectChunk();
                break;
            }
            case 'Tab':
            break;

            case 'Esc':
            case 'Escape':
            this.blur();
            break;

            case '.':
            case this.decimalSeparator(): {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;
                const value = input.value;
                const sepIndex = value.indexOf(this.decimalSeparator());
                if (sepIndex === -1) return;
                input.setSelectionRange(sepIndex + 1, sepIndex + 2);
                this.selectChunk();
                break;
            }

            case 'Backspace': {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;

                const value = input.value;
                const sepIndex = value.indexOf(this.decimalSeparator()) + 1; // position after decimal separator
                let { start, end } = this.caret();
                let newValue: string;

                if (start !== end) {
                    newValue = value.substring(0, start) + "0" + value.substring(end);
                    this.writeValue(this.parseStringToNumber(newValue));
                    input.setSelectionRange(start, start);
                    return;
                }

                if (sepIndex === -1 || start <= sepIndex) { // left of decimal
                    if (start === 0) return;
                    const skipDecimal = start === sepIndex;
                    if (skipDecimal) start -= 1;
                    newValue = value.substring(0, start - 1) + value.substring(start);
                    this.writeValue(this.parseStringToNumber(newValue));
                    const diff = this.formatValue(this.parseStringToNumber(newValue)).length - value.length + start - end;
                    const newCaret = Math.max(0, start + diff + (skipDecimal ? 1 : 0));
                    input.setSelectionRange(newCaret, newCaret);
                } else { // right of decimal
                    if (start <= sepIndex || start > value.length) return;
                    newValue = value.substring(0, start - 1) + "0" + value.substring(start);
                    this.writeValue(this.parseStringToNumber(newValue));
                    input.setSelectionRange(start - 1, start - 1);
                }
                break;
            }

            default: {
                event.preventDefault();
                const input = this.inputElement();
                if (!input) return;
                if (event.key === '-') {
                    const { start, end } = this.caret();
                    const value = input.value;
                    const numberValue = this.parseStringToNumber(value);
                    this.writeValue(numberValue * -1);
                    input.setSelectionRange(start, end);
                    this.selectChunk();
                    break;
                }
                if (!/^\d$/.test(event.key)) return;

                const value = input.value;
                const sepIndex = value.indexOf(this.decimalSeparator());
                const { start, end } = this.caret();

                let newValue: string;

                const isBeforeDecimal = sepIndex === -1 || start <= sepIndex;

                if (isBeforeDecimal) {
                    newValue = value.substring(0, start) + event.key + value.substring(end);
                } else {
                    if (start < value.length)
                        newValue = value.substring(0, start) + event.key + value.substring(start + 1);
                    else
                        // newValue = value + event.key; // Allows shifting digits
                        return;
                }
                this.writeValue(this.parseStringToNumber(newValue));
                if (isBeforeDecimal) {
                    // Changing characters might add or remove thousands separators, so we need to calculate the new caret position
                    // based on the difference in length between the old and new formatted value and the original caret position/selection.
                    const diff = this.formatValue(this.parseStringToNumber(newValue)).length - value.length + start - end;
                    const newCaret = Math.max(1, start + diff);
                    input.setSelectionRange(newCaret, newCaret);
                } else {
                    input.setSelectionRange(start + 1, start + 1);
                    // select chunk only if not at the end to allow appending digits
                    if (start + 1 < input.value.length)
                        this.selectChunk();
                }
                break;
            }
        }
    }

    public onMouseUp(event: MouseEvent){
        event.preventDefault();
        this.selectChunk();
    }

    public previousChunk() {
        const input = this.inputElement();
        if (!input) return;
        const value = input.value;
        const sepIndex = value.indexOf(this.decimalSeparator());
        if (sepIndex === -1) {
            input.setSelectionRange(0, value.length);
            return;
        }
        const cursor = this.caret().end;
        if (cursor > sepIndex)
            input.setSelectionRange(0, sepIndex);
        else
            input.setSelectionRange(0, 0);
    }

    public nextChunk() {
        const input = this.inputElement();
        if (!input) return;
        const value = input.value;
        const sepIndex = value.indexOf(this.decimalSeparator());
        if (sepIndex === -1) {
            input.setSelectionRange(0, value.length);
            return;
        }
        const cursor = this.caret().end;
        if (cursor <= sepIndex) {
            const start = sepIndex + 1;
            input.setSelectionRange(start, start + 1);
        } else {
            const end = value.length;
            input.setSelectionRange(end, end);
        }
    }

    public onUserInput(event: Event) {
        const inputVal = (event.target as HTMLInputElement).value;
        const digits = inputVal.replace(/\D/g, '');
        let intVal = 0;
        if (digits.length > 0)
            intVal = parseInt(digits, 10);


        if (this.value !== intVal) {
            const old = this.value;
            this.value = intVal;
            this.control?.setValue(intVal, {emitEvent: false});
            this.onModelChange?.(intVal);
            this.changed.emit([old, intVal]);
        }

        this.updateInputfield(intVal);
        this.onInput.emit(event);
        this.nextChunk();
    }

    public onCopy(event: ClipboardEvent){
        const numberValue = this.scaleValueToInt(this.value, this.fractions);
        event.clipboardData?.setData("text/plain", numberValue.toFixed(this.fractions));
        event.preventDefault();
    }


    public handleInputChange(event: ClipboardEvent) {
        event.preventDefault();
        const text = event.clipboardData?.getData("text/plain") ?? "";

        const normalized = this.normalize(text);
        const intVal = Math.round(parseFloat(normalized) * Math.pow(10, this.fractions));
        this.writeValue(intVal);
    }

    public writeValue(value: number | null | undefined): void {
        if (![null, undefined].includes(value) && !Number.isSafeInteger(value))
            throw new Error("Value for VInputCurrencyComponent must be an integer representing the smallest currency unit (e.g., cents).");
        super.writeValue(value);
        this.updateInputfield(value);
    }

    public onClearClick(event: MouseEvent): void {
        event.stopPropagation();
        this.clear();
        if (this.inputElement())
            this.inputElement().value = '';
    }

    private selectChunk() {
        const input = this.inputElement();
        if (!input) return;
        const value = input.value;
        const sepIndex = value.indexOf(this.decimalSeparator());
        if (sepIndex === -1) { // fallback if no decimal separator found
            input.setSelectionRange(0, value.length);
            return;
        }
        const {start: caretStart} = this.caret();
        if (caretStart <= sepIndex) {
            input.setSelectionRange(0, sepIndex);
        } else {
            const start = sepIndex + 1;
            const end = value.length;
            let cursorPos = caretStart ?? start;
            if (cursorPos > sepIndex && cursorPos < end)
                input.setSelectionRange(cursorPos, cursorPos + 1);
            else
                input.setSelectionRange(end - 1, end);
        }
    }

    private caret() {
        const input = this.inputElement();
        if (!input) return {start: 0, end: 0};
        return {start: input.selectionStart ?? 0, end: input.selectionEnd ?? 0};
    }

    private parseStringToNumber(value: string): number {
        const isNegative = value.startsWith('-');
        let digits = value.replace(/\D/g, '');
        let intVal = 0;
        if (digits.length > 0)
            intVal = parseInt(digits, 10);
        return isNegative ? -intVal : intVal;
    }


    private normalize(value: string): string {
        let normalized = value.replace(/[^0-9.\-]/g, '');
        const lastDot = normalized.lastIndexOf('.');
        if (lastDot !== -1)
            normalized = normalized.replace(/\./g, (m, i) => i === lastDot ? '.' : '');
        return normalized;
    }

    private updateInputfield(val: number) {
        if (!this.inputElement()) return;
        this.inputElement().value = this.formatValue(val);
    }

    private formatValue(val: number): string {
        if (!this.inputElement()) return '';

        const currentValue = this.inputElement().value;

        // A programmatic reset (control set to null/undefined) must empty the field - keeping the
        // current text would show a value the control no longer holds.
        if (val === null || val === undefined)
            return '';
        if (isNaN(val))
            return currentValue;
        const scaledValue = this.scaleValueToInt(val, this.fractions);
        let result;
        try {
            const decimalFormatter = new Intl.NumberFormat(this.locale(), {
                style:                 'decimal',
                minimumFractionDigits: this.fractions,
                maximumFractionDigits: this.fractions
            });
            result = decimalFormatter.format(scaledValue);
        } catch(e) {
            result = scaledValue.toFixed(this.fractions);
        }
        return result;
    }

    private scaleValueToInt(value: number, fractions: number): number {
        return value / Math.pow(10, fractions);
    }

    private get fractions(): number {
        return getFractions(this.appService.defaultCurrency);
    }
}
